import {InvoiceLine, type InvoiceLineData} from "@/types/invoices/InvoiceLine.ts";
import {InternationalPod, type InternationalPodData} from "@/types/pods/InternationalPod.ts";
import {InternationalRoadTransportOrder, type InternationalRoadTransportOrderData} from "@/types/order-entry/InternationalRoadTransportOrder.ts";
import {InternationalRoadShipmentGood, type InternationalRoadShipmentGoodData} from "@/types/shipments/International/InternationalRoadShipmentGood.ts";
import {Company,type CompanyData} from "@/types/company/company.ts";
import {InternationalRoadShipmentActivity, type InternationalRoadShipmentActivityData} from "@/types/shipments/International/InternationalRoadShipmentActivity.ts";
import {InternationalRoadShipmentTrackingUrl, type InternationalRoadShipmentTrackingUrlData} from "@/types/order-entry/InternationalRoadShipmentTrackingUrl.ts";
import {OrderData, type OrderDataData} from "@/types/order-entry/OrderData.ts";
import {ApiStatus, type ApiStatusData} from "@/types/site_configuration/ApiStatus.ts";

export class InternationalShipment {
    public id: number | null;
    public consignor_company_code: string;
    public company_name_1: string;
    public client_name: string;
    public admin_name: string;
    public perm_shipment_id: number | null;
    public temp_shipment_id: number;
    public reference: string;
    public reference_2: string;
    public financial_reference_1: string;
    public uit_code: string;
    public shipment_date: Date | null;
    public order_time: string;
    public shipment_status: string;
    public loading_name_1: string;
    public loading_address_1: string;
    public loading_city_name: string;
    public loading_country_code: string;
    public loading_zipcode: string;
    public loading_county_code: string;
    public loading_start_date: Date | null;
    public loading_start_time: string;
    public loading_act_start_date: Date | null;
    public loading_act_start_time: string;
    public loading_contact_person: string;
    public loading_phone: string;
    public unloading_name_1: string;
    public unloading_address_1: string;
    public unloading_city_name: string;
    public unloading_country_code: string;
    public unloading_zipcode: string;
    public unloading_county_code: string;
    public unloading_start_date: Date | null;
    public unloading_start_time: string;
    public unloading_act_start_date: Date | null;
    public unloading_act_start_time: string;
    public unloading_contact_person: string;
    public unloading_phone: string;
    public quantity: number;
    public package_code: string;
    public est_weight: string;
    public est_volume: string;
    public last_modified: string;
    public unload_delay_kpi: string;
    public department: string;
    public packs_count: object;
    public has_labels: boolean;
    public time_start_loading: string;
    public time_end_loading: string;
    public time_start_unloading: string;
    public time_end_unloading: string;
    public createdAt: Date | null;
    public invoiceLines: InvoiceLine[]|undefined;
    public orderData: OrderData[];
    public transportOrder: InternationalRoadTransportOrder | null;
    public shipmentGoods:InternationalRoadShipmentGood[];
    public shipmentActivities: InternationalRoadShipmentActivity[];
    public internationalRoadShipmentTrackingUrl: InternationalRoadShipmentTrackingUrl | null;
    public apiStatus: ApiStatus | null;
    public company: Company | null;
    public internationalPod: InternationalPod | null;

    constructor(data: InternationalShipmentData | void) {
        if (data === undefined) {
            this.id = null;
            this.consignor_company_code = "";
            this.company_name_1 = "";
            this.client_name = "";
            this.admin_name = "";
            this.perm_shipment_id = -1;
            this.temp_shipment_id = -1;
            this.reference = "";
            this.reference_2 = "";
            this.financial_reference_1 = "";
            this.uit_code = "";
            this.shipment_date = null;
            this.order_time = "";
            this.shipment_status = "";
            this.loading_name_1 = "";
            this.loading_address_1 = "";
            this.loading_city_name = "";
            this.loading_country_code = "";
            this.loading_zipcode = "";
            this.loading_county_code = "";
            this.loading_start_date = null;
            this.loading_start_time = "";
            this.loading_act_start_date = null;
            this.loading_act_start_time = "";
            this.loading_contact_person = "";
            this.loading_phone = "";
            this.unloading_name_1 = "";
            this.unloading_address_1 = "";
            this.unloading_city_name = "";
            this.unloading_country_code = "";
            this.unloading_zipcode = "";
            this.unloading_county_code = "";
            this.unloading_start_date = null;
            this.unloading_start_time = "";
            this.unloading_act_start_date = null;
            this.unloading_act_start_time = "";
            this.unloading_contact_person = "";
            this.unloading_phone = "";
            this.quantity = 0;
            this.package_code = "";
            this.est_weight = "";
            this.est_volume = "";
            this.last_modified = "";
            this.unload_delay_kpi = "";
            this.department = "";
            this.packs_count = {};
            this.has_labels = false;
            this.time_start_loading = "";
            this.time_end_loading = "";
            this.time_start_unloading = "";
            this.time_end_unloading = "";
            this.createdAt = null;
            this.invoiceLines = [];
            this.orderData = [];
            this.transportOrder = null;
            this.shipmentGoods = [];
            this.shipmentActivities = [];
            this.internationalRoadShipmentTrackingUrl = null;
            this.apiStatus = null;
            this.company = null;
            this.internationalPod = null;
        } else {
            this.id = data.id;
            this.consignor_company_code = data.consignor_company_code;
            this.company_name_1 = data.company_name_1;
            this.client_name = data.client_name;
            this.admin_name = data.admin_name;
            this.perm_shipment_id = data.perm_shipment_id;
            this.temp_shipment_id = data.temp_shipment_id;
            this.reference = data.reference;
            this.reference_2 = data.reference_2;
            this.financial_reference_1 = data.financial_reference_1;
            this.uit_code = data.uit_code;

            this.shipment_date = null;
            if (data.shipment_date !== null) {
                this.shipment_date = new Date(data.shipment_date);
            }
            this.order_time = data.order_time;

            this.shipment_status = data.shipment_status;
            this.loading_name_1 = data.loading_name_1;
            this.loading_address_1 = data.loading_address_1;
            this.loading_city_name = data.loading_city_name;
            this.loading_country_code = data.loading_country_code;
            this.loading_zipcode = data.loading_zipcode;
            this.loading_county_code = data.loading_county_code;

            this.loading_start_date = null;
            if (data.loading_start_date !== null) {
                this.loading_start_date = new Date(data.loading_start_date);
            }

            this.loading_start_time = data.loading_start_time;

            this.loading_act_start_date = null;
            if (data.loading_act_start_date !== null) {
                this.loading_act_start_date = new Date(data.loading_act_start_date);
            }
            this.loading_act_start_time = data.loading_act_start_time;
            this.loading_contact_person = data.loading_contact_person;
            this.loading_phone = data.loading_phone;
            this.unloading_name_1 = data.unloading_name_1;
            this.unloading_address_1 = data.unloading_address_1;
            this.unloading_city_name = data.unloading_city_name;
            this.unloading_country_code = data.unloading_country_code;
            this.unloading_zipcode = data.unloading_zipcode;
            this.unloading_county_code = data.unloading_county_code;

            this.unloading_start_date = null;
            if (data.unloading_start_date !== null) {
                this.unloading_start_date = new Date(data.unloading_start_date);
            }

            this.unloading_start_time = data.unloading_start_time;

            this.unloading_act_start_date = null;
            if (data.unloading_act_start_date !== null) {
                this.unloading_act_start_date = new Date(data.unloading_act_start_date);
            }

            this.unloading_act_start_time = data.unloading_act_start_time;
            this.unloading_contact_person = data.unloading_contact_person;
            this.unloading_phone = data.unloading_phone;
            this.quantity = data.quantity;
            this.package_code = data.package_code;
            this.est_weight = data.est_weight;
            this.est_volume = data.est_volume;
            this.last_modified = data.last_modified;
            this.unload_delay_kpi = data.unload_delay_kpi;
            this.department = data.department;
            this.packs_count = data.packs_count;
            this.has_labels = data.has_labels;
            this.time_start_loading = data.time_start_loading;
            this.time_end_loading = data.time_end_loading;
            this.time_start_unloading = data.time_start_unloading;
            this.time_end_unloading = data.time_end_unloading;
            this.createdAt = null;
            if (data.createdAt !== null) {
                this.createdAt = new Date(data.createdAt);
            }
            this.invoiceLines = [];
            if (data.invoiceLines !== undefined && data.invoiceLines !== null) {
                data.invoiceLines.forEach(invoiceLine => {
                    this.invoiceLines.push(new InvoiceLine(invoiceLine));
                })
            }
            this.orderData = [];
            if (data.orderData !== undefined && data.orderData !== null) {
                data.orderData.forEach(orderData => {
                    this.orderData.push(new OrderData(orderData));
                })
            }
            this.transportOrder = null;
            if (data.transportOrder !== undefined && data.transportOrder !== null) {
                this.transportOrder = new InternationalRoadTransportOrder(data.transportOrder)
            }
            this.shipmentGoods = [];
            if(data.shipmentGoods !== undefined && data.shipmentGoods !== null)
            {
                data.shipmentGoods.forEach(shipmentGood => {
                    this.shipmentGoods.push(new InternationalRoadShipmentGood(shipmentGood));
                })
            }
            this.shipmentActivities = [];
            if (data.shipmentActivities !== undefined && data.shipmentActivities !== null) {
                data.shipmentActivities.forEach(shipmentActivity => {
                    this.shipmentActivities.push(new InternationalRoadShipmentActivity(shipmentActivity));
                })
            }
            this.internationalRoadShipmentTrackingUrl = null;
            if (data.internationalRoadShipmentTrackingUrl !== undefined && data.internationalRoadShipmentTrackingUrl !== null) {
                this.internationalRoadShipmentTrackingUrl = new InternationalRoadShipmentTrackingUrl(data.internationalRoadShipmentTrackingUrl)
            }
            this.apiStatus = null;
            if (data.apiStatus !== undefined && data.apiStatus !== null) {
                this.apiStatus = new ApiStatus(data.apiStatus)
            }
            this.company = null;
            if (data.company !== undefined && data.company !== null) {
                this.company = new Company(data.company)
            }
            this.internationalPod = null;
            if (data.internationalPod !== undefined && data.internationalPod !== null) {
                this.internationalPod = new InternationalPod(data.internationalPod)
            }
        }
    }
}


export type InternationalShipmentData = {
    id: number | null;
    consignor_company_code: string;
    company_name_1: string;
    client_name: string;
    admin_name: string;
    perm_shipment_id: number | null;
    temp_shipment_id: number;
    reference: string;
    reference_2: string;
    financial_reference_1: string;
    uit_code: string;
    shipment_date: Date | null;
    order_time: string;
    shipment_status: string;
    loading_name_1: string;
    loading_address_1: string;
    loading_city_name: string;
    loading_country_code: string;
    loading_zipcode: string;
    loading_county_code: string;
    loading_start_date: Date | null;
    loading_start_time: string;
    loading_act_start_date: Date | null;
    loading_act_start_time: string;
    loading_contact_person: string;
    loading_phone: string;
    unloading_name_1: string;
    unloading_address_1: string;
    unloading_city_name: string;
    unloading_country_code: string;
    unloading_zipcode: string;
    unloading_county_code: string;
    unloading_start_date: Date | null;
    unloading_start_time: string;
    unloading_act_start_date: Date | null;
    unloading_act_start_time: string;
    unloading_contact_person: string;
    unloading_phone: string;
    quantity: number;
    package_code: string;
    est_weight: string;
    est_volume: string;
    last_modified: string;
    unload_delay_kpi: string;
    department: string;
    packs_count:object;
    has_labels:boolean;
    time_start_loading: string;
    time_end_loading: string;
    time_start_unloading: string;
    time_end_unloading: string;
    createdAt: Date | null;
    invoiceLines: InvoiceLineData[]|undefined;
    orderData: OrderDataData[] | undefined;
    transportOrder: InternationalRoadTransportOrderData | null;
    shipmentGoods:InternationalRoadShipmentGoodData[]|undefined;
    shipmentActivities: InternationalRoadShipmentActivityData[] | undefined;
    internationalRoadShipmentTrackingUrl: InternationalRoadShipmentTrackingUrlData | null;
    apiStatus: ApiStatusData | null;
    company: CompanyData | null;
    internationalPod: InternationalPodData | null;
}
